import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  Show,
  type Accessor,
  type JSX,
} from "solid-js";
import {
  api,
  type TaskQueueExecutionRun,
  type TaskQueueExecutionRunDetail,
  type TaskQueueEvent,
  type TaskQueueCursorPage,
  type TaskQueueScheduler,
  type TaskQueueSchedulerHealth,
  type TaskQueueSchedulerHourlyStat,
  type TaskQueueSchedulerInput,
  type TaskQueueSchedulerDelta,
  type TaskQueueSchedulerLiveUpdate,
  type TaskQueueSchedulerTarget,
  type TaskQueueDriverState,
  type TaskQueueDriverMode,
  type TaskQueueSchedulerTick,
  type TaskQueueTestDelayUnit,
} from "../shared";
import {
  QUEUE_CONTROL_CLASS,
  QueueField,
  QueueMetric,
  QueueSectionHeading,
  QueueStatusBadge,
} from "./TaskQueueUi";

const PAGE_SIZES = [10, 25, 50, 100];
type HistoryCleanupRequest = {
  recordType: "logical_executions" | "platform_wakeups";
  activity: "empty" | "work" | "all";
};

function emptyScheduler(): TaskQueueSchedulerInput {
  return {
    name: "",
    target: "vps",
    enabled: true,
    scheduleType: "interval",
    intervalSeconds: 60,
    cronExpression: null,
    queueName: null,
    batchSizeOverride: null,
    timeBudgetMsOverride: null,
  };
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : "Never";
}

function scheduleLabel(scheduler: TaskQueueScheduler) {
  if (scheduler.scheduleType === "cron")
    return `${scheduler.cronExpression} UTC`;
  const seconds = scheduler.intervalSeconds ?? 0;
  return seconds < 60
    ? `Every ${seconds}s`
    : seconds % 3600 === 0
      ? `Every ${seconds / 3600}h`
      : `Every ${seconds / 60}m`;
}

function isPast(value: string | null | undefined, graceMs = 0) {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp + graceMs < Date.now();
}

function driverIsStale(
  health: TaskQueueSchedulerHealth,
  driver?: TaskQueueDriverState,
) {
  if (health.enabledSchedulers === 0) return false;
  if (driver?.mode === "adaptive" && driver.lastStatus !== "failed")
    return false;
  const configuredMs =
    driver?.mode === "fixed" ? driver.fixedIntervalSeconds * 2_000 : 0;
  const allowedSilenceMs = Math.max(
    configuredMs,
    health.target === "cloudflare" ? 150_000 : 15_000,
  );
  return !health.lastTickAt || isPast(health.lastTickAt, allowedSilenceMs);
}

export function TaskQueueSchedulersPanel(props: {
  event: Accessor<TaskQueueEvent | null>;
  delta: Accessor<TaskQueueSchedulerDelta | null>;
  bootstrapRevision: Accessor<number>;
  eventCursor: Accessor<number>;
  queueNames: Accessor<string[]>;
}) {
  const [schedulers, setSchedulers] = createSignal<TaskQueueScheduler[]>([]);
  const [drivers, setDrivers] = createSignal<TaskQueueDriverState[]>([]);
  const [health, setHealth] = createSignal<TaskQueueSchedulerHealth[]>([]);
  const [hourly, setHourly] = createSignal<TaskQueueSchedulerHourlyStat[]>([]);
  const [ticks, setTicks] =
    createSignal<TaskQueueCursorPage<TaskQueueSchedulerTick> | null>(null);
  const [runs, setRuns] =
    createSignal<TaskQueueCursorPage<TaskQueueExecutionRun> | null>(null);
  const [tickPage, setTickPage] = createSignal(1);
  const [runPage, setRunPage] = createSignal(1);
  const [tickCursors, setTickCursors] = createSignal<Array<string | null>>([
    null,
  ]);
  const [runCursors, setRunCursors] = createSignal<Array<string | null>>([
    null,
  ]);
  const [perPage, setPerPage] = createSignal(10);
  const [targetFilter, setTargetFilter] = createSignal<
    "" | TaskQueueSchedulerTarget
  >("");
  const [workFilter, setWorkFilter] = createSignal<"" | "yes" | "no">("");
  const [statusFilter, setStatusFilter] = createSignal("");
  const [triggerFilter, setTriggerFilter] = createSignal("");
  const [queueFilter, setQueueFilter] = createSignal("");
  const [schedulerFilter, setSchedulerFilter] = createSignal("");
  const [workerFilter, setWorkerFilter] = createSignal("");
  const [fromFilter, setFromFilter] = createSignal("");
  const [toFilter, setToFilter] = createSignal("");
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [form, setForm] =
    createSignal<TaskQueueSchedulerInput>(emptyScheduler());
  const [intervalValue, setIntervalValue] = createSignal(1);
  const [intervalUnit, setIntervalUnit] = createSignal<
    "seconds" | "minutes" | "hours"
  >("minutes");
  const [preview, setPreview] = createSignal<string[]>([]);
  const [confirmDelete, setConfirmDelete] =
    createSignal<TaskQueueScheduler | null>(null);
  const [confirmHistoryCleanup, setConfirmHistoryCleanup] =
    createSignal<HistoryCleanupRequest | null>(null);
  const [selectedRun, setSelectedRun] =
    createSignal<TaskQueueExecutionRunDetail | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal("");
  const [error, setError] = createSignal("");
  const [showFilters, setShowFilters] = createSignal(false);
  const [newUpdates, setNewUpdates] = createSignal(0);
  const [refreshRevision, setRefreshRevision] = createSignal(0);
  const [testQueueName, setTestQueueName] = createSignal("operator-tests");
  const [testDelayValue, setTestDelayValue] = createSignal(30);
  const [testDelayUnit, setTestDelayUnit] =
    createSignal<TaskQueueTestDelayUnit>("seconds");
  const [testTaskLabel, setTestTaskLabel] = createSignal("");
  let changeCursor = 0;
  let lastLoadKey = "";
  let activeLoad: AbortController | null = null;

  async function load() {
    activeLoad?.abort();
    const controller = new AbortController();
    activeLoad = controller;
    setError("");
    try {
      const filters = {
        target: targetFilter() || undefined,
        status: statusFilter() || undefined,
        workerId: workerFilter() || undefined,
        from: fromFilter() ? new Date(fromFilter()).toISOString() : undefined,
        to: toFilter() ? new Date(toFilter()).toISOString() : undefined,
        work: workFilter() || undefined,
        perPage: perPage(),
      };
      const [
        nextSchedulers,
        nextDrivers,
        nextHealth,
        nextHourly,
        nextTicks,
        nextRuns,
      ] = await Promise.all([
        api.taskQueue.schedulers(
          targetFilter() || undefined,
          controller.signal,
        ),
        api.taskQueue.drivers(controller.signal),
        api.taskQueue.schedulerHealth(controller.signal),
        api.taskQueue.schedulerHourly(controller.signal),
        api.taskQueue.schedulerTicks(
          { ...filters, cursor: tickCursors().at(-1) ?? undefined },
          controller.signal,
        ),
        api.taskQueue.executionRuns(
          {
            ...filters,
            schedulerId: schedulerFilter() || undefined,
            queueName: queueFilter() || undefined,
            triggerKind: triggerFilter() || undefined,
            cursor: runCursors().at(-1) ?? undefined,
          },
          controller.signal,
        ),
      ]);
      setSchedulers(nextSchedulers);
      setDrivers(nextDrivers);
      setHealth(nextHealth);
      setHourly(nextHourly);
      setTicks(nextTicks);
      setRuns(nextRuns);
      changeCursor = Math.max(
        changeCursor,
        props.eventCursor(),
        nextTicks.changeCursor,
        nextRuns.changeCursor,
      );
      setNewUpdates(0);
    } catch (cause) {
      if ((cause as { name?: string } | null)?.name === "AbortError") return;
      setError(
        cause instanceof Error
          ? cause.message
          : "Failed to load scheduler activity",
      );
    } finally {
      if (activeLoad === controller) activeLoad = null;
    }
  }

  onCleanup(() => activeLoad?.abort());

  createEffect(() => {
    const bootstrapRevision = props.bootstrapRevision();
    if (bootstrapRevision === 0) return;
    const loadKey = JSON.stringify([
      bootstrapRevision,
      targetFilter(),
      workFilter(),
      statusFilter(),
      triggerFilter(),
      queueFilter(),
      schedulerFilter(),
      workerFilter(),
      fromFilter(),
      toFilter(),
      tickPage(),
      runPage(),
      perPage(),
      refreshRevision(),
      tickCursors().at(-1) ?? null,
      runCursors().at(-1) ?? null,
    ]);
    if (loadKey === lastLoadKey) return;
    lastLoadKey = loadKey;
    void load();
  });

  const matchesScheduler = (item: TaskQueueScheduler) =>
    !targetFilter() || item.target === targetFilter();
  const matchesTick = (item: TaskQueueSchedulerTick) =>
    (!targetFilter() || item.target === targetFilter()) &&
    (!statusFilter() || item.status === statusFilter()) &&
    (!workerFilter() || item.workerId === workerFilter()) &&
    (!workFilter() ||
      (workFilter() === "yes" ? item.claimed > 0 : item.claimed === 0)) &&
    (!fromFilter() || item.startedAt >= new Date(fromFilter()).toISOString()) &&
    (!toFilter() || item.startedAt <= new Date(toFilter()).toISOString());
  const matchesRun = (item: TaskQueueExecutionRun) =>
    (!targetFilter() || item.target === targetFilter()) &&
    (!statusFilter() || item.status === statusFilter()) &&
    (!workerFilter() || item.workerId === workerFilter()) &&
    (!triggerFilter() || item.triggerKind === triggerFilter()) &&
    (!queueFilter() || item.queueName === queueFilter()) &&
    (!schedulerFilter() || item.schedulerId === schedulerFilter()) &&
    (!workFilter() ||
      (workFilter() === "yes" ? item.claimed > 0 : item.claimed === 0)) &&
    (!fromFilter() || item.startedAt >= new Date(fromFilter()).toISOString()) &&
    (!toFilter() || item.startedAt <= new Date(toFilter()).toISOString());

  function upsertScheduler(item: TaskQueueScheduler) {
    setSchedulers((current) => {
      const next = current.filter((value) => value.id !== item.id);
      if (matchesScheduler(item)) next.push(item);
      return next.sort((left, right) => left.name.localeCompare(right.name));
    });
  }

  function removeScheduler(id: string) {
    setSchedulers((current) => current.filter((item) => item.id !== id));
  }

  function upsertActivity<T extends { id: string; startedAt: string }>(
    page: TaskQueueCursorPage<T> | null,
    item: T,
    matches: boolean,
    newestPage: boolean,
  ): TaskQueueCursorPage<T> | null {
    if (!page) return page;
    const existed = page.items.some((value) => value.id === item.id);
    if (!newestPage) {
      if (!existed) setNewUpdates((value) => value + 1);
      return page;
    }
    const items = page.items.filter((value) => value.id !== item.id);
    if (matches) items.push(item);
    items.sort(
      (left, right) =>
        right.startedAt.localeCompare(left.startedAt) ||
        right.id.localeCompare(left.id),
    );
    const total = Math.max(
      0,
      page.total + (!existed && matches ? 1 : existed && !matches ? -1 : 0),
    );
    return {
      ...page,
      items: items.slice(0, perPage()),
      total,
      hasNext: total > perPage(),
    };
  }

  function removeActivity<T extends { id: string }>(
    page: TaskQueueCursorPage<T> | null,
    id: string,
  ) {
    if (!page) return page;
    const existed = page.items.some((item) => item.id === id);
    return existed
      ? {
          ...page,
          items: page.items.filter((item) => item.id !== id),
          total: Math.max(0, page.total - 1),
        }
      : page;
  }

  function mergeHourly(changes: TaskQueueSchedulerHourlyStat[]) {
    if (!changes.length) return;
    setHourly((current) => {
      const key = (item: TaskQueueSchedulerHourlyStat) =>
        `${item.bucketStart}:${item.target}:${item.schedulerId}`;
      const byKey = new Map(current.map((item) => [key(item), item]));
      for (const item of changes) byKey.set(key(item), item);
      return [...byKey.values()]
        .sort((left, right) =>
          right.bucketStart.localeCompare(left.bucketStart),
        )
        .slice(0, 168);
    });
  }

  function applyLiveUpdate(update: TaskQueueSchedulerLiveUpdate) {
    if (update.driver)
      setDrivers((current) =>
        [
          ...current.filter((item) => item.target !== update.driver!.target),
          update.driver!,
        ].sort((a, b) => a.target.localeCompare(b.target)),
      );
    if (update.scheduler) upsertScheduler(update.scheduler);
    if (update.removedSchedulerId) removeScheduler(update.removedSchedulerId);
    if (update.tick)
      setTicks((current) =>
        upsertActivity(
          current,
          update.tick!,
          matchesTick(update.tick!),
          tickPage() === 1,
        ),
      );
    if (update.removedTickId)
      setTicks((current) => removeActivity(current, update.removedTickId!));
    if (update.run)
      setRuns((current) =>
        upsertActivity(
          current,
          update.run!,
          matchesRun(update.run!),
          runPage() === 1,
        ),
      );
    if (update.removedRunId)
      setRuns((current) => removeActivity(current, update.removedRunId!));
    if (update.health) setHealth(update.health);
    if (update.hourly) mergeHourly(update.hourly);
  }

  createEffect(() => {
    const event = props.event();
    if (!event || event.id <= changeCursor) return;
    const monitor = event.payload?.monitor;
    if (!monitor || typeof monitor !== "object") return;
    applyLiveUpdate(monitor as TaskQueueSchedulerLiveUpdate);
    changeCursor = event.id;
  });

  createEffect(() => {
    const delta = props.delta();
    if (!delta || delta.changeCursor <= changeCursor) return;
    if (delta.resyncRequired) {
      lastLoadKey = "";
      void load();
      return;
    }
    for (const item of delta.schedulers) upsertScheduler(item);
    for (const id of delta.removedSchedulerIds) removeScheduler(id);
    for (const item of delta.ticks)
      setTicks((current) =>
        upsertActivity(current, item, matchesTick(item), tickPage() === 1),
      );
    for (const id of delta.removedTickIds)
      setTicks((current) => removeActivity(current, id));
    for (const item of delta.runs)
      setRuns((current) =>
        upsertActivity(current, item, matchesRun(item), runPage() === 1),
      );
    for (const id of delta.removedRunIds)
      setRuns((current) => removeActivity(current, id));
    if (delta.health) setHealth(delta.health);
    if (delta.hourly) mergeHourly(delta.hourly);
    changeCursor = delta.changeCursor;
  });

  function beginEdit(scheduler?: TaskQueueScheduler) {
    setPreview([]);
    if (!scheduler) {
      setEditingId("new");
      setForm(emptyScheduler());
      setIntervalValue(1);
      setIntervalUnit("minutes");
      return;
    }
    setEditingId(scheduler.id);
    setForm({
      name: scheduler.name,
      target: scheduler.target,
      enabled: scheduler.enabled,
      scheduleType: scheduler.scheduleType,
      intervalSeconds: scheduler.intervalSeconds,
      cronExpression: scheduler.cronExpression,
      queueName: scheduler.queueName,
      batchSizeOverride: scheduler.batchSizeOverride,
      timeBudgetMsOverride: scheduler.timeBudgetMsOverride,
    });
    const seconds = scheduler.intervalSeconds ?? 60;
    if (seconds % 3600 === 0) {
      setIntervalValue(seconds / 3600);
      setIntervalUnit("hours");
    } else if (seconds % 60 === 0) {
      setIntervalValue(seconds / 60);
      setIntervalUnit("minutes");
    } else {
      setIntervalValue(seconds);
      setIntervalUnit("seconds");
    }
  }

  function normalizedForm(): TaskQueueSchedulerInput {
    const multiplier =
      intervalUnit() === "hours" ? 3600 : intervalUnit() === "minutes" ? 60 : 1;
    return {
      ...form(),
      intervalSeconds:
        form().scheduleType === "interval"
          ? intervalValue() * multiplier
          : null,
    };
  }

  async function save() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const value = normalizedForm();
      const scheduler =
        editingId() === "new"
          ? await api.taskQueue.createScheduler(value)
          : await api.taskQueue.updateScheduler(editingId()!, value);
      upsertScheduler(scheduler);
      setMessage(
        editingId() === "new" ? "Scheduler created." : "Scheduler updated.",
      );
      setEditingId(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to save scheduler",
      );
    } finally {
      setBusy(false);
    }
  }

  async function runAction(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(success);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Scheduler action failed",
      );
    } finally {
      setBusy(false);
    }
  }

  async function cleanHistory(request: HistoryCleanupRequest) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api.taskQueue.cleanupExecutionHistory({
        recordTypes: [request.recordType],
        statuses:
          request.activity === "all"
            ? ["completed", "failed", "interrupted", "skipped_overlap"]
            : ["completed"],
        emptyOnly: request.activity === "empty",
        workOnly: request.activity === "work",
        allMatches: true,
        scheduledOnly:
          request.recordType === "logical_executions" &&
          request.activity !== "all",
        olderThan: new Date().toISOString(),
        limit: 500,
      });
      const label =
        request.recordType === "logical_executions"
          ? request.activity === "all"
            ? "logical execution"
            : `${request.activity === "empty" ? "empty" : "non-empty"} scheduled run`
          : request.activity === "all"
            ? "platform wake-up"
            : `${request.activity === "empty" ? "idle" : "non-empty"} platform wake-up`;
      setMessage(
        `Deleted ${result.deleted.toLocaleString()} ${label} record(s) from ${result.matched.toLocaleString()} eligible.`,
      );
      resetActivityPages();
      setRefreshRevision((value) => value + 1);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "History cleanup failed",
      );
    } finally {
      setBusy(false);
    }
  }

  const testTaskAvailableAt = () => {
    const multipliers: Record<TaskQueueTestDelayUnit, number> = {
      seconds: 1_000,
      minutes: 60_000,
      hours: 3_600_000,
      days: 86_400_000,
    };
    return new Date(
      Date.now() + Math.max(0, testDelayValue()) * multipliers[testDelayUnit()],
    );
  };
  const testDelayMaximum = () =>
    ({ seconds: 31_536_000, minutes: 525_600, hours: 8_760, days: 365 })[
      testDelayUnit()
    ];

  async function enqueueTestTask() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api.taskQueue.enqueueTestTask({
        queueName: testQueueName(),
        delayValue: testDelayValue(),
        delayUnit: testDelayUnit(),
        label: testTaskLabel() || null,
      });
      setMessage(
        `Test task ${result.task.id} scheduled for ${formatDate(result.scheduledFor)} in ${result.task.queueName}.`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Failed to schedule test task",
      );
    } finally {
      setBusy(false);
    }
  }

  const maxHourly = () =>
    Math.max(1, ...hourly().map((item) => item.executionCount));
  const activeFilterCount = () =>
    [
      targetFilter(),
      workFilter(),
      statusFilter(),
      triggerFilter(),
      queueFilter(),
      schedulerFilter(),
      workerFilter(),
      fromFilter(),
      toFilter(),
    ].filter(Boolean).length;
  const clearFilters = () => {
    setTargetFilter("");
    setStatusFilter("");
    setWorkFilter("");
    setTriggerFilter("");
    setQueueFilter("");
    setSchedulerFilter("");
    setWorkerFilter("");
    setFromFilter("");
    setToFilter("");
    resetActivityPages();
  };
  const resetActivityPages = () => {
    setTickCursors([null]);
    setRunCursors([null]);
    setTickPage(1);
    setRunPage(1);
  };
  const nextTickPage = () => {
    const cursor = ticks()?.nextCursor;
    if (!cursor) return;
    setTickCursors((items) => [...items, cursor]);
    setTickPage((value) => value + 1);
  };
  const previousTickPage = () => {
    if (tickPage() <= 1) return;
    setTickCursors((items) => items.slice(0, -1));
    setTickPage((value) => value - 1);
  };
  const nextRunPage = () => {
    const cursor = runs()?.nextCursor;
    if (!cursor) return;
    setRunCursors((items) => [...items, cursor]);
    setRunPage((value) => value + 1);
  };
  const previousRunPage = () => {
    if (runPage() <= 1) return;
    setRunCursors((items) => items.slice(0, -1));
    setRunPage((value) => value - 1);
  };

  return (
    <div class="space-y-5">
      <Show when={message()}>
        <Notice tone="success" text={message()} />
      </Show>
      <Show when={error()}>
        <div class="flex items-center justify-between gap-3 rounded-xl border border-red-800/70 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          <span>{error()}</span>
          <button
            onClick={() => void load()}
            class="rounded-lg border border-red-700 px-3 py-2 font-semibold hover:bg-red-900/50"
          >
            Retry
          </button>
        </div>
      </Show>
      <Show when={newUpdates() > 0}>
        <div class="flex items-center justify-between gap-3 rounded-xl border border-cyan-800 bg-cyan-950/35 px-4 py-3 text-sm text-cyan-100">
          <span>
            {newUpdates()} live update burst(s) arrived while viewing older
            activity.
          </span>
          <button
            onClick={() => {
              resetActivityPages();
              void load();
            }}
            class="rounded-lg bg-cyan-700 px-3 py-2 font-semibold hover:bg-cyan-600"
          >
            Return to newest
          </button>
        </div>
      </Show>

      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <QueueSectionHeading
          eyebrow="Operations overview"
          title="Scheduler health"
          description="See whether platform drivers are waking, schedules are due, and executions are currently active."
          action={
            <button
              onClick={() => beginEdit()}
              class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-950/30 hover:bg-cyan-500"
            >
              New scheduler
            </button>
          }
        />
        <div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <QueueMetric
            label="Enabled schedules"
            value={schedulers().filter((item) => item.enabled).length}
            detail={`${schedulers().length} definitions configured`}
            tone="cyan"
          />
          <QueueMetric
            label="Due now"
            value={health().reduce(
              (total, item) => total + item.dueSchedulers,
              0,
            )}
            detail="Waiting for a platform driver"
            tone="amber"
          />
          <QueueMetric
            label="Active drivers"
            value={health().reduce(
              (total, item) => total + item.runningTicks,
              0,
            )}
            detail="Wake-ups currently processing"
            tone="green"
          />
          <QueueMetric
            label="Driver failures"
            value={health().reduce(
              (total, item) => total + item.consecutiveFailures,
              0,
            )}
            detail="Consecutive failures across targets"
            tone={
              health().some((item) => item.consecutiveFailures)
                ? "red"
                : "neutral"
            }
          />
        </div>
        <div class="mt-4 grid gap-3 lg:grid-cols-2">
          <For each={health()}>
            {(item) => (
              <article
                class={`rounded-xl border p-4 ${
                  driverIsStale(
                    item,
                    drivers().find((driver) => driver.target === item.target),
                  )
                    ? "border-amber-800/70 bg-amber-950/20"
                    : "border-gray-800 bg-gray-950/45"
                }`}
              >
                <div class="flex items-center justify-between gap-3">
                  <div class="flex items-center gap-2">
                    <span
                      class={`h-2.5 w-2.5 rounded-full ${
                        item.runningTicks
                          ? "animate-pulse bg-cyan-400"
                          : item.consecutiveFailures
                            ? "bg-red-400"
                            : driverIsStale(
                                  item,
                                  drivers().find(
                                    (driver) => driver.target === item.target,
                                  ),
                                )
                              ? "bg-amber-400"
                              : "bg-emerald-400"
                      }`}
                    />
                    <h4 class="font-semibold uppercase tracking-wide text-gray-200">
                      {item.target} driver
                    </h4>
                  </div>
                  <QueueStatusBadge
                    status={
                      item.runningTicks
                        ? "running"
                        : driverIsStale(
                              item,
                              drivers().find(
                                (driver) => driver.target === item.target,
                              ),
                            )
                          ? "overdue"
                          : "healthy"
                    }
                  />
                </div>
                <div class="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <span class="text-xs text-gray-600">
                      Last platform wake-up
                    </span>
                    <div class="mt-1 text-gray-300">
                      {formatDate(item.lastTickAt)}
                    </div>
                  </div>
                  <div>
                    <span class="text-xs text-gray-600">
                      Last execution with work
                    </span>
                    <div class="mt-1 text-gray-300">
                      {formatDate(item.lastWorkAt)}
                    </div>
                  </div>
                </div>
                <Show
                  when={driverIsStale(
                    item,
                    drivers().find((driver) => driver.target === item.target),
                  )}
                >
                  <p class="mt-3 text-xs leading-5 text-amber-300">
                    No recent platform wake-up. Schedules are durable, but no
                    healthy worker is currently advancing them.
                  </p>
                </Show>
              </article>
            )}
          </For>
        </div>
        <div class="mt-5 grid gap-4 lg:grid-cols-2">
          <For each={drivers()}>
            {(driver) => (
              <DriverCard
                driver={driver}
                busy={busy()}
                onSave={(mode, fixedIntervalSeconds) =>
                  runAction(
                    () =>
                      api.taskQueue.updateDriver(driver.target, {
                        mode,
                        fixedIntervalSeconds,
                      }),
                    `${driver.target.toUpperCase()} driver settings saved.`,
                  )
                }
                onWake={() =>
                  runAction(
                    () => api.taskQueue.wakeDriver(driver.target),
                    `${driver.target.toUpperCase()} driver wake requested.`,
                  )
                }
              />
            )}
          </For>
        </div>
      </section>

      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <QueueSectionHeading
          eyebrow="Runner test"
          title="Schedule a test task"
          description="Create a safe no-op task that becomes available in the future. The adaptive driver should move its next wake to this task and process it through the selected queue."
        />
        <div class="mt-5 grid gap-4 lg:grid-cols-[minmax(12rem,1fr)_minmax(8rem,0.55fr)_minmax(9rem,0.65fr)]">
          <QueueField label="Queue">
            <input
              list="task-queue-test-queues"
              value={testQueueName()}
              onInput={(event) => setTestQueueName(event.currentTarget.value)}
              placeholder="operator-tests"
              class={QUEUE_CONTROL_CLASS}
            />
            <datalist id="task-queue-test-queues">
              <option value="operator-tests" />
              <For each={props.queueNames()}>
                {(name) => <option value={name} />}
              </For>
            </datalist>
          </QueueField>
          <QueueField label="Delay">
            <input
              type="number"
              min="0"
              max={testDelayMaximum()}
              step="1"
              value={testDelayValue()}
              onInput={(event) =>
                setTestDelayValue(Number(event.currentTarget.value))
              }
              class={QUEUE_CONTROL_CLASS}
            />
          </QueueField>
          <QueueField label="Unit">
            <select
              value={testDelayUnit()}
              onChange={(event) =>
                setTestDelayUnit(
                  event.currentTarget.value as TaskQueueTestDelayUnit,
                )
              }
              class={QUEUE_CONTROL_CLASS}
            >
              <option value="seconds">Seconds</option>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </QueueField>
        </div>
        <div class="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <QueueField label="Optional label">
            <input
              value={testTaskLabel()}
              maxlength="200"
              onInput={(event) => setTestTaskLabel(event.currentTarget.value)}
              placeholder="Why this runner test was scheduled"
              class={QUEUE_CONTROL_CLASS}
            />
          </QueueField>
          <button
            disabled={
              busy() ||
              !testQueueName().trim() ||
              !Number.isFinite(testDelayValue()) ||
              testDelayValue() < 0 ||
              testDelayValue() > testDelayMaximum()
            }
            onClick={() => void enqueueTestTask()}
            class="h-11 rounded-lg bg-cyan-700 px-5 text-sm font-semibold text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Schedule test task
          </button>
        </div>
        <div class="mt-4 rounded-xl border border-gray-800 bg-gray-950/45 px-4 py-3 text-sm text-gray-400">
          Available at{" "}
          <span class="font-medium text-gray-200">
            {testTaskAvailableAt().toLocaleString()}
          </span>
          . A zero-second delay tests immediate driver wake-up.
        </div>
      </section>

      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <QueueSectionHeading
          eyebrow="Configuration"
          title="Schedule definitions"
          description="Each definition decides when a driver drains all queues or one named queue."
        />
        <div class="mt-5 grid gap-4 xl:grid-cols-2">
          <For each={schedulers()}>
            {(scheduler) => (
              <SchedulerCard
                scheduler={scheduler}
                driver={drivers().find(
                  (item) => item.target === scheduler.target,
                )}
                busy={busy()}
                onRun={() =>
                  void runAction(
                    () => api.taskQueue.runScheduler(scheduler.id),
                    `Scheduler ${scheduler.name} completed its immediate run.`,
                  )
                }
                onToggle={() =>
                  void runAction(
                    () =>
                      api.taskQueue.updateScheduler(scheduler.id, {
                        enabled: !scheduler.enabled,
                      }),
                    scheduler.enabled
                      ? "Scheduler paused."
                      : "Scheduler resumed.",
                  )
                }
                onEdit={() => beginEdit(scheduler)}
                onDuplicate={() => {
                  beginEdit(scheduler);
                  setEditingId("new");
                  setForm((current) => ({
                    ...current,
                    name: `${current.name} copy`,
                  }));
                }}
                onDelete={() => setConfirmDelete(scheduler)}
              />
            )}
          </For>
          <Show when={!schedulers().length}>
            <div class="col-span-full rounded-xl border border-dashed border-gray-700 px-6 py-10 text-center">
              <div class="font-medium text-gray-300">
                No schedulers configured
              </div>
              <p class="mt-1 text-sm text-gray-500">
                Create a definition to begin draining queued work automatically.
              </p>
            </div>
          </Show>
        </div>
      </section>

      <Show when={editingId()}>
        <SchedulerEditor
          editingId={editingId()!}
          form={form()}
          intervalValue={intervalValue()}
          intervalUnit={intervalUnit()}
          preview={preview()}
          busy={busy()}
          queueNames={props.queueNames()}
          onForm={setForm}
          onIntervalValue={setIntervalValue}
          onIntervalUnit={setIntervalUnit}
          onCancel={() => setEditingId(null)}
          onSave={() => void save()}
          onPreview={() =>
            void api.taskQueue
              .previewScheduler(normalizedForm())
              .then((result) => setPreview(result.nextRuns))
              .catch((cause) => setError(cause.message))
          }
        />
      </Show>

      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <QueueSectionHeading
          eyebrow="Traceability"
          title="Execution activity"
          description="Platform wake-ups are driver checks, including idle checks. Logical executions are actual queue-processing runs and can be opened to inspect task attempts."
          action={
            <div class="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => setShowFilters(!showFilters())}
                class={`rounded-lg border px-3 py-2 text-sm ${showFilters() || activeFilterCount() ? "border-cyan-700 bg-cyan-950/30 text-cyan-300" : "border-gray-700 text-gray-300 hover:bg-gray-800"}`}
              >
                Filters{activeFilterCount() ? ` (${activeFilterCount()})` : ""}
              </button>
              <select
                aria-label="Activity rows per page"
                value={perPage()}
                onChange={(event) => {
                  setPerPage(Number(event.currentTarget.value));
                  resetActivityPages();
                }}
                class="h-10 rounded-lg border border-gray-700 bg-gray-950 px-3 text-sm text-gray-200"
              >
                <For each={PAGE_SIZES}>
                  {(size) => <option value={size}>{size} rows</option>}
                </For>
              </select>
            </div>
          }
        />
        <Show when={showFilters()}>
          <ActivityFilters
            target={targetFilter()}
            status={statusFilter()}
            work={workFilter()}
            trigger={triggerFilter()}
            queue={queueFilter()}
            scheduler={schedulerFilter()}
            worker={workerFilter()}
            from={fromFilter()}
            to={toFilter()}
            schedulers={schedulers()}
            queueNames={props.queueNames()}
            activeCount={activeFilterCount()}
            onTarget={(value) => {
              setTargetFilter(value);
              resetActivityPages();
            }}
            onStatus={(value) => {
              setStatusFilter(value);
              resetActivityPages();
            }}
            onWork={(value) => {
              setWorkFilter(value);
              resetActivityPages();
            }}
            onTrigger={(value) => {
              setTriggerFilter(value);
              resetActivityPages();
            }}
            onQueue={(value) => {
              setQueueFilter(value);
              resetActivityPages();
            }}
            onScheduler={(value) => {
              setSchedulerFilter(value);
              resetActivityPages();
            }}
            onWorker={setWorkerFilter}
            onFrom={setFromFilter}
            onTo={setToFilter}
            onClear={clearFilters}
          />
        </Show>
        <div class="mt-5 grid gap-5 xl:grid-cols-2">
          <ActivityList
            title="Platform wake-ups"
            description="Physical VPS ticks and Cloudflare scheduled events, including idle checks."
            total={ticks()?.total ?? 0}
            action={
              <HistoryCleanupActions
                recordType="platform_wakeups"
                busy={busy()}
                onRequest={(request) => setConfirmHistoryCleanup(request)}
              />
            }
            page={tickPage()}
            hasNext={Boolean(ticks()?.hasNext)}
            onPrevious={previousTickPage}
            onNext={nextTickPage}
          >
            <For each={ticks()?.items ?? []}>
              {(item) => (
                <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-4 py-3.5 text-sm">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="font-medium uppercase text-gray-200">
                        {item.target}
                      </span>
                      <span class="truncate text-xs text-gray-500">
                        {item.workerId}
                      </span>
                    </div>
                    <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span>{formatDate(item.startedAt)}</span>
                      <span>{item.dueSchedulers} due</span>
                      <span>{item.claimed} claimed</span>
                      <span>{item.durationMs}ms</span>
                    </div>
                  </div>
                  <QueueStatusBadge status={item.status} />
                </div>
              )}
            </For>
            <Show when={!ticks()?.items.length}>
              <EmptyActivity text="No platform wake-ups match these filters." />
            </Show>
          </ActivityList>
          <ActivityList
            title="Logical executions"
            description="Queue-processing runs. Open a row to inspect linked attempts and tasks."
            total={runs()?.total ?? 0}
            action={
              <HistoryCleanupActions
                recordType="logical_executions"
                busy={busy()}
                onRequest={(request) => setConfirmHistoryCleanup(request)}
              />
            }
            page={runPage()}
            hasNext={Boolean(runs()?.hasNext)}
            onPrevious={previousRunPage}
            onNext={nextRunPage}
          >
            <For each={runs()?.items ?? []}>
              {(item) => (
                <button
                  onClick={() =>
                    void api.taskQueue
                      .executionRun(item.id)
                      .then(setSelectedRun)
                      .catch((cause) => setError(cause.message))
                  }
                  class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-4 py-3.5 text-left text-sm transition hover:bg-gray-800/45"
                >
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="font-medium capitalize text-gray-200">
                        {item.triggerKind}
                      </span>
                      <span class="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                        Scope: {item.queueName ?? "All queues"}
                      </span>
                      <For each={item.taskSummary?.queueNames ?? []}>
                        {(queueName) => (
                          <span class="rounded border border-cyan-900/70 bg-cyan-950/40 px-1.5 py-0.5 text-xs text-cyan-300">
                            Claimed queue: {queueName}
                          </span>
                        )}
                      </For>
                    </div>
                    <Show when={item.taskSummary?.labels.length}>
                      <div class="mt-1.5 flex flex-wrap gap-1.5">
                        <For each={item.taskSummary.labels}>
                          {(label) => (
                            <span
                              class="max-w-full truncate rounded bg-gray-800/80 px-2 py-1 text-xs text-gray-300"
                              title={label}
                            >
                              Label: {label}
                            </span>
                          )}
                        </For>
                      </div>
                    </Show>
                    <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span>Started {formatDate(item.startedAt)}</span>
                      <Show when={item.scheduledFor}>
                        <span>Scheduled {formatDate(item.scheduledFor)}</span>
                      </Show>
                      <Show when={item.schedulingDelayMs != null}>
                        <span>{item.schedulingDelayMs}ms scheduling delay</span>
                      </Show>
                      <span>{item.claimed} claimed</span>
                      <span>{item.completed} completed</span>
                      <span>{item.durationMs}ms</span>
                    </div>
                    <div class="mt-1 truncate font-mono text-[10px] text-gray-700">
                      {item.id}
                    </div>
                  </div>
                  <QueueStatusBadge status={item.status} />
                </button>
              )}
            </For>
            <Show when={!runs()?.items.length}>
              <EmptyActivity text="No logical executions match these filters." />
            </Show>
          </ActivityList>
        </div>
      </section>

      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <QueueSectionHeading
          eyebrow="Long-term signal"
          title="Hourly scheduler activity"
          description="Execution volume across the latest 48 recorded hourly buckets. Hover a bar for latency percentiles."
        />
        <div class="mt-5 flex h-36 items-end gap-1 overflow-hidden rounded-xl border border-gray-800 bg-gray-950/45 px-3 pt-4">
          <For each={hourly().slice(0, 48).reverse()}>
            {(item) => (
              <div
                title={`${item.bucketStart}: ${item.executionCount} executions, ${item.completed} completed, p50 ${item.p50DurationMs}ms, p95 ${item.p95DurationMs}ms`}
                class="min-w-2 flex-1 rounded-t bg-gradient-to-t from-cyan-700 to-cyan-400 transition hover:brightness-125"
                style={{
                  height: `${Math.max(3, (item.executionCount / maxHourly()) * 100)}%`,
                }}
              />
            )}
          </For>
        </div>
        <div class="mt-2 text-xs text-gray-600">
          Raw activity is retained for 7 days; hourly aggregates are retained
          for one year.
        </div>
      </section>

      <Show when={confirmDelete()}>
        {(scheduler) => (
          <div class="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4">
            <div class="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
              <div class="text-xs font-semibold uppercase tracking-[0.16em] text-red-400">
                Destructive action
              </div>
              <h4 class="mt-1 font-semibold text-gray-100">
                Delete scheduler?
              </h4>
              <p class="mt-2 text-sm leading-6 text-gray-400">
                Delete "{scheduler().name}"? Existing execution history will
                remain available.
              </p>
              <div class="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  class="rounded-lg border border-gray-700 px-4 py-2 text-gray-200 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const value = scheduler();
                    setConfirmDelete(null);
                    void runAction(
                      () => api.taskQueue.deleteScheduler(value.id),
                      "Scheduler deleted.",
                    );
                  }}
                  class="rounded-lg bg-red-700 px-4 py-2 text-white hover:bg-red-600"
                >
                  Delete scheduler
                </button>
              </div>
            </div>
          </div>
        )}
      </Show>
      <Show when={confirmHistoryCleanup()}>
        {(request) => (
          <div class="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4">
            <div class="w-full max-w-lg rounded-2xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
              <div class="text-xs font-semibold uppercase tracking-[0.16em] text-amber-400">
                Execution history cleanup
              </div>
              <h4 class="mt-1 font-semibold text-gray-100">
                Clear{" "}
                {request().activity === "all"
                  ? "all terminal"
                  : request().activity === "empty"
                    ? "empty"
                    : "non-empty"}{" "}
                {request().recordType === "logical_executions"
                  ? request().activity === "all"
                    ? "logical executions"
                    : "scheduled runs"
                  : "platform wake-ups"}
                ?
              </h4>
              <p class="mt-2 text-sm leading-6 text-gray-400">
                This removes all matching records in bounded database batches.{" "}
                {request().activity === "all"
                  ? "Completed, failed, interrupted, and overlap records are removed regardless of trigger or claimed work."
                  : request().activity === "empty"
                    ? "Only completed records that claimed no tasks are removed."
                    : "Only completed records that claimed one or more tasks are removed."}{" "}
                Currently running records are always preserved.
              </p>
              <div class="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmHistoryCleanup(null)}
                  class="rounded-lg border border-gray-700 px-4 py-2 text-gray-200 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const value = request();
                    setConfirmHistoryCleanup(null);
                    void cleanHistory(value);
                  }}
                  class="rounded-lg bg-amber-700 px-4 py-2 font-medium text-white hover:bg-amber-600"
                >
                  Clear all matching
                </button>
              </div>
            </div>
          </div>
        )}
      </Show>
      <Show when={selectedRun()}>
        {(run) => (
          <ExecutionModal run={run()} onClose={() => setSelectedRun(null)} />
        )}
      </Show>
    </div>
  );
}

function DriverCard(props: {
  driver: TaskQueueDriverState;
  busy: boolean;
  onSave: (mode: TaskQueueDriverMode, interval: number) => Promise<void>;
  onWake: () => Promise<void>;
}) {
  const [editing, setEditing] = createSignal(false);
  const [mode, setMode] = createSignal<TaskQueueDriverMode>(props.driver.mode);
  const [interval, setInterval] = createSignal(
    props.driver.fixedIntervalSeconds,
  );
  createEffect(() => {
    if (!editing()) {
      setMode(props.driver.mode);
      setInterval(props.driver.fixedIntervalSeconds);
    }
  });
  return (
    <article class="rounded-xl border border-gray-700 bg-gray-950/55 p-4">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-400">
            {props.driver.target} driver
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-2">
            <h4 class="font-semibold text-gray-100">Wake strategy</h4>
            <span class="rounded-md bg-gray-800 px-2 py-1 text-xs capitalize text-gray-300">
              {props.driver.mode}
              {props.driver.mode === "fixed"
                ? ` · ${props.driver.fixedIntervalSeconds}s`
                : ""}
            </span>
          </div>
        </div>
        <QueueStatusBadge status={props.driver.lastStatus} />
      </div>
      <DriverCountdown driver={props.driver} />
      <Show when={props.driver.uncoveredQueues.length}>
        <div class="mt-3 rounded-lg border border-amber-800/60 bg-amber-950/25 px-3 py-2 text-xs text-amber-200">
          No enabled scheduler covers: {props.driver.uncoveredQueues.join(", ")}
        </div>
      </Show>
      <div class="mt-4 grid gap-2 text-xs text-gray-500 sm:grid-cols-2">
        <div>
          Last wake
          <div class="mt-1 text-gray-300">
            {formatDate(props.driver.lastWakeAt)}
          </div>
        </div>
        <div>
          Last result
          <div class="mt-1 text-gray-300">
            {props.driver.lastResult
              ? `${props.driver.lastResult.completed} completed, ${props.driver.lastResult.failed} failed`
              : "No execution yet"}
          </div>
        </div>
      </div>
      <Show when={editing()}>
        <div class="mt-4 rounded-xl border border-cyan-900/60 bg-cyan-950/15 p-4">
          <div class="grid gap-3 sm:grid-cols-2">
            <QueueField label="Driver mode">
              <select
                value={mode()}
                onChange={(event) =>
                  setMode(event.currentTarget.value as TaskQueueDriverMode)
                }
                class={QUEUE_CONTROL_CLASS}
              >
                <option value="adaptive">Adaptive</option>
                <option value="fixed">Fixed cadence</option>
              </select>
            </QueueField>
            <Show when={mode() === "fixed"}>
              <QueueField label="Cadence (seconds)">
                <input
                  type="number"
                  min={props.driver.target === "vps" ? 5 : 60}
                  value={interval()}
                  onInput={(event) =>
                    setInterval(Number(event.currentTarget.value))
                  }
                  class={QUEUE_CONTROL_CLASS}
                />
              </QueueField>
            </Show>
          </div>
          <p class="mt-3 text-xs leading-5 text-gray-500">
            {mode() === "adaptive"
              ? "Sleeps while idle and wakes immediately when work becomes available."
              : "Checks for due work at the configured cadence."}
          </p>
        </div>
      </Show>
      <div class="mt-4 flex flex-wrap gap-2 border-t border-gray-800 pt-4">
        <Show when={!editing()}>
          <button
            onClick={() => setEditing(true)}
            class="rounded-lg border border-gray-700 px-3 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-800"
          >
            Edit settings
          </button>
        </Show>
        <Show when={editing()}>
          <button
            disabled={props.busy}
            onClick={async () => {
              await props.onSave(mode(), interval());
              if (!props.busy) setEditing(false);
            }}
            class="rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-40"
          >
            Save settings
          </button>
          <button
            onClick={() => {
              setMode(props.driver.mode);
              setInterval(props.driver.fixedIntervalSeconds);
              setEditing(false);
            }}
            class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
          >
            Cancel
          </button>
        </Show>
        <button
          disabled={props.busy}
          onClick={props.onWake}
          class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 disabled:opacity-40"
        >
          Wake now
        </button>
      </div>
    </article>
  );
}

function DriverCountdown(props: { driver: TaskQueueDriverState }) {
  const [now, setNow] = createSignal(Date.now());
  const timer = window.setInterval(() => setNow(Date.now()), 500);
  onCleanup(() => window.clearInterval(timer));
  const remaining = () =>
    props.driver.nextWakeAt
      ? Math.max(0, Date.parse(props.driver.nextWakeAt) - now())
      : null;
  const total = () =>
    props.driver.mode === "fixed"
      ? props.driver.fixedIntervalSeconds * 1000
      : Math.max(
          1_000,
          (props.driver.nextWakeAt
            ? Date.parse(props.driver.nextWakeAt)
            : now()) -
            (props.driver.lastWakeAt
              ? Date.parse(props.driver.lastWakeAt)
              : now()),
        );
  const progress = () =>
    remaining() == null
      ? 0
      : Math.max(0, Math.min(100, (1 - remaining()! / total()) * 100));
  return (
    <div class="mt-4 rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2.5">
      <div class="mb-2 flex justify-between gap-2 text-xs">
        <span class="text-gray-500">
          Next driver wake · {props.driver.nextWakeReason.replaceAll("_", " ")}
        </span>
        <span class="text-cyan-300">
          {remaining() == null
            ? "Waiting for work"
            : remaining()! <= 0
              ? "Wake due"
              : `${Math.ceil(remaining()! / 1000)}s`}
        </span>
      </div>
      <div class="h-2 overflow-hidden rounded-full bg-gray-800">
        <div
          class="h-full rounded-full bg-cyan-500 transition-[width] duration-500"
          style={{ width: `${progress()}%` }}
        />
      </div>
    </div>
  );
}

function SchedulerCard(props: {
  scheduler: TaskQueueScheduler;
  driver?: TaskQueueDriverState;
  busy: boolean;
  onRun: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const dormant = () =>
    props.driver?.mode === "adaptive" && !props.driver.nextWakeAt;
  const overdue = () =>
    props.scheduler.enabled &&
    !dormant() &&
    isPast(props.scheduler.nextRunAt, 2_000);
  return (
    <article
      class={`rounded-xl border p-4 ${overdue() ? "border-amber-800/70 bg-amber-950/20" : props.scheduler.enabled ? "border-gray-700 bg-gray-950/45" : "border-gray-800 bg-gray-950/25"}`}
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="font-semibold text-gray-100">{props.scheduler.name}</h4>
            <QueueStatusBadge
              status={
                overdue()
                  ? "overdue"
                  : props.scheduler.enabled
                    ? "enabled"
                    : "paused"
              }
            />
          </div>
          <div class="mt-3 flex flex-wrap gap-2 text-xs text-gray-400">
            <span class="rounded-md bg-gray-800/80 px-2 py-1">
              Logical frequency: {scheduleLabel(props.scheduler)}
            </span>
            <span class="rounded-md bg-gray-800/80 px-2 py-1">
              {props.scheduler.queueName ?? "All queues"}
            </span>
            <span class="rounded-md bg-gray-800/80 px-2 py-1 uppercase">
              {props.scheduler.target}
            </span>
          </div>
          <div class="mt-3 text-xs text-gray-500">
            Driver strategy:{" "}
            {props.driver?.mode === "fixed"
              ? `fixed every ${props.driver.fixedIntervalSeconds}s`
              : "adaptive; immediate for new work"}
          </div>
        </div>
        <div class="grid shrink-0 gap-2 text-right text-xs text-gray-500">
          <div>
            Last logical run
            <div class="mt-1 text-gray-300">
              {formatDate(props.scheduler.lastRunAt)}
            </div>
          </div>
          <div>
            {overdue() ? "Next logical run overdue" : "Next logical run"}
            <div
              class={`mt-1 ${overdue() ? "text-amber-300" : "text-gray-300"}`}
            >
              {formatDate(props.scheduler.nextRunAt)}
            </div>
          </div>
        </div>
      </div>
      <SchedulerCountdown
        scheduler={props.scheduler}
        dormant={dormant()}
        paused={!props.scheduler.enabled}
      />
      <Show when={overdue()}>
        <p class="mt-3 text-xs leading-5 text-amber-300">
          The logical due time has passed. Check driver health above; WebSocket
          connectivity only observes work and does not execute it.
        </p>
      </Show>
      <div class="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-800 pt-4">
        <button
          disabled={props.busy}
          onClick={props.onRun}
          class="rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-40"
        >
          Run now
        </button>
        <button
          onClick={props.onEdit}
          class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
        >
          Edit
        </button>
        <button
          onClick={props.onToggle}
          class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
        >
          {props.scheduler.enabled
            ? "Pause logical schedule"
            : "Resume logical schedule"}
        </button>
        <div class="ml-auto flex gap-1">
          <button
            onClick={props.onDuplicate}
            class="rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            Duplicate
          </button>
          <button
            onClick={props.onDelete}
            class="rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-red-950/40"
          >
            Delete
          </button>
        </div>
      </div>
    </article>
  );
}

function SchedulerCountdown(props: {
  scheduler: TaskQueueScheduler;
  dormant?: boolean;
  paused?: boolean;
}) {
  const [now, setNow] = createSignal(Date.now());
  const timer = window.setInterval(() => setNow(Date.now()), 250);
  onCleanup(() => window.clearInterval(timer));

  const timing = () => {
    const next = Date.parse(props.scheduler.nextRunAt);
    const intervalMs =
      props.scheduler.scheduleType === "interval"
        ? Math.max(1_000, (props.scheduler.intervalSeconds ?? 1) * 1_000)
        : Math.max(
            1_000,
            next -
              Date.parse(
                props.scheduler.lastRunAt ?? props.scheduler.updatedAt,
              ),
          );
    const start = props.scheduler.lastRunAt
      ? Date.parse(props.scheduler.lastRunAt)
      : next - intervalMs;
    const remainingMs = Math.max(0, next - now());
    const progress = Math.max(
      0,
      Math.min(100, ((now() - start) / Math.max(1, next - start)) * 100),
    );
    return { remainingMs, progress };
  };
  const remainingLabel = () => {
    if (props.paused) return "Paused";
    if (props.dormant) return "Waiting for work";
    const seconds = Math.ceil(timing().remainingMs / 1_000);
    if (seconds <= 0) return "Waiting for worker";
    if (seconds < 60) return `${seconds}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}m ${remainder}s remaining`;
  };

  return (
    <div class="mt-4 rounded-lg border border-gray-800 bg-gray-950/45 px-3 py-2.5">
      <div class="mb-2 flex items-center justify-between gap-3 text-xs">
        <span class="text-gray-500">Automatic run progress</span>
        <span
          class={
            timing().remainingMs === 0 ? "text-amber-300" : "text-cyan-300"
          }
        >
          {remainingLabel()}
        </span>
      </div>
      <div
        class="h-1.5 overflow-hidden rounded-full bg-gray-800"
        role="progressbar"
        aria-label="Time until next automatic scheduler run"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={
          props.dormant || props.paused ? 0 : Math.round(timing().progress)
        }
      >
        <div
          class={`h-full rounded-full transition-[width] duration-200 ${timing().remainingMs === 0 && !props.dormant && !props.paused ? "bg-amber-400" : "bg-cyan-500"}`}
          style={{
            width: `${props.dormant || props.paused ? 0 : timing().progress}%`,
          }}
        />
      </div>
    </div>
  );
}

function SchedulerEditor(props: {
  editingId: string;
  form: TaskQueueSchedulerInput;
  intervalValue: number;
  intervalUnit: "seconds" | "minutes" | "hours";
  preview: string[];
  busy: boolean;
  queueNames: string[];
  onForm: (value: TaskQueueSchedulerInput) => void;
  onIntervalValue: (value: number) => void;
  onIntervalUnit: (value: "seconds" | "minutes" | "hours") => void;
  onCancel: () => void;
  onSave: () => void;
  onPreview: () => void;
}) {
  return (
    <section class="rounded-2xl border border-cyan-800/70 bg-gradient-to-br from-cyan-950/25 to-gray-900 p-5 shadow-xl shadow-black/10">
      <QueueSectionHeading
        eyebrow={
          props.editingId === "new" ? "New definition" : "Edit definition"
        }
        title={
          props.editingId === "new" ? "Create scheduler" : "Update scheduler"
        }
        description="Choose the platform cadence and queue scope. Optional limits override the global scheduled execution defaults."
        action={
          <button
            onClick={props.onCancel}
            class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </button>
        }
      />
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <QueueField label="Name">
          <input
            value={props.form.name}
            onInput={(event) =>
              props.onForm({ ...props.form, name: event.currentTarget.value })
            }
            class={QUEUE_CONTROL_CLASS}
          />
        </QueueField>
        <QueueField label="Execution target">
          <select
            value={props.form.target}
            onChange={(event) =>
              props.onForm({
                ...props.form,
                target: event.currentTarget.value as TaskQueueSchedulerTarget,
              })
            }
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="vps">VPS</option>
            <option value="cloudflare">Cloudflare</option>
          </select>
        </QueueField>
        <QueueField label="Schedule type">
          <select
            value={props.form.scheduleType}
            onChange={(event) =>
              props.onForm({
                ...props.form,
                scheduleType: event.currentTarget.value as "interval" | "cron",
              })
            }
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="interval">Repeat interval</option>
            <option value="cron">Cron expression (UTC)</option>
          </select>
        </QueueField>
        <QueueField label="Queue scope">
          <select
            value={props.form.queueName ?? ""}
            onChange={(event) =>
              props.onForm({
                ...props.form,
                queueName: event.currentTarget.value || null,
              })
            }
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All queues</option>
            <For each={props.queueNames}>
              {(name) => <option value={name}>{name}</option>}
            </For>
          </select>
        </QueueField>
        <Show
          when={props.form.scheduleType === "interval"}
          fallback={
            <QueueField
              label="Five-field cron"
              hint="Evaluated in UTC. Example: */5 * * * *"
              class="md:col-span-2"
            >
              <input
                value={props.form.cronExpression ?? ""}
                placeholder="*/5 * * * *"
                onInput={(event) =>
                  props.onForm({
                    ...props.form,
                    cronExpression: event.currentTarget.value,
                  })
                }
                class={`${QUEUE_CONTROL_CLASS} font-mono`}
              />
            </QueueField>
          }
        >
          <QueueField label="Repeat every">
            <input
              type="number"
              min="1"
              value={props.intervalValue}
              onInput={(event) =>
                props.onIntervalValue(Number(event.currentTarget.value))
              }
              class={QUEUE_CONTROL_CLASS}
            />
          </QueueField>
          <QueueField label="Interval unit">
            <select
              value={props.intervalUnit}
              onChange={(event) =>
                props.onIntervalUnit(
                  event.currentTarget.value as "seconds" | "minutes" | "hours",
                )
              }
              class={QUEUE_CONTROL_CLASS}
            >
              <option value="seconds">Seconds</option>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
            </select>
          </QueueField>
        </Show>
        <QueueField
          label="Tasks per execution"
          hint="Leave empty to use the global scheduled default."
        >
          <input
            type="number"
            min="1"
            placeholder="Use global default"
            value={props.form.batchSizeOverride ?? ""}
            onInput={(event) =>
              props.onForm({
                ...props.form,
                batchSizeOverride: event.currentTarget.value
                  ? Number(event.currentTarget.value)
                  : null,
              })
            }
            class={QUEUE_CONTROL_CLASS}
          />
        </QueueField>
        <QueueField
          label="Runtime budget"
          hint="Leave empty to use the global scheduled default."
        >
          <div class="relative">
            <input
              type="number"
              min="250"
              placeholder="Use global default"
              value={props.form.timeBudgetMsOverride ?? ""}
              onInput={(event) =>
                props.onForm({
                  ...props.form,
                  timeBudgetMsOverride: event.currentTarget.value
                    ? Number(event.currentTarget.value)
                    : null,
                })
              }
              class={`${QUEUE_CONTROL_CLASS} pr-10`}
            />
            <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">
              ms
            </span>
          </div>
        </QueueField>
      </div>
      <div class="mt-5 rounded-xl border border-gray-800 bg-gray-950/40 px-4 py-3 text-xs leading-5 text-gray-500">
        VPS intervals may run every 5 seconds. Cloudflare definitions are
        evaluated by a one-minute platform wake-up, so their effective minimum
        interval is one minute.
      </div>
      <div class="mt-5 flex flex-wrap gap-2">
        <button
          disabled={props.busy}
          onClick={props.onSave}
          class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500 disabled:opacity-40"
        >
          Save scheduler
        </button>
        <button
          onClick={props.onPreview}
          class="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
        >
          Preview next 5 runs
        </button>
      </div>
      <Show when={props.preview.length}>
        <div class="mt-4 grid gap-2 text-sm text-gray-300 sm:grid-cols-2 xl:grid-cols-5">
          <For each={props.preview}>
            {(value) => (
              <div class="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
                {formatDate(value)}
              </div>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function ActivityFilters(props: {
  target: "" | TaskQueueSchedulerTarget;
  status: string;
  work: "" | "yes" | "no";
  trigger: string;
  queue: string;
  scheduler: string;
  worker: string;
  from: string;
  to: string;
  schedulers: TaskQueueScheduler[];
  queueNames: string[];
  activeCount: number;
  onTarget: (value: "" | TaskQueueSchedulerTarget) => void;
  onStatus: (value: string) => void;
  onWork: (value: "" | "yes" | "no") => void;
  onTrigger: (value: string) => void;
  onQueue: (value: string) => void;
  onScheduler: (value: string) => void;
  onWorker: (value: string) => void;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <div class="mt-5 rounded-xl border border-gray-800 bg-gray-950/45 p-4">
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <QueueField label="Target">
          <select
            value={props.target}
            onChange={(event) =>
              props.onTarget(
                event.currentTarget.value as "" | TaskQueueSchedulerTarget,
              )
            }
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All targets</option>
            <option value="vps">VPS</option>
            <option value="cloudflare">Cloudflare</option>
          </select>
        </QueueField>
        <QueueField label="Status">
          <select
            value={props.status}
            onChange={(event) => props.onStatus(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All statuses</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="interrupted">Interrupted</option>
            <option value="skipped_overlap">Skipped overlap</option>
          </select>
        </QueueField>
        <QueueField label="Work outcome">
          <select
            value={props.work}
            onChange={(event) =>
              props.onWork(event.currentTarget.value as "" | "yes" | "no")
            }
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All activity</option>
            <option value="yes">Work performed</option>
            <option value="no">Idle only</option>
          </select>
        </QueueField>
        <QueueField label="Trigger">
          <select
            value={props.trigger}
            onChange={(event) => props.onTrigger(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All triggers</option>
            <option value="scheduled">Scheduled</option>
            <option value="request">Request</option>
            <option value="manual">Manual</option>
            <option value="maintenance">Maintenance</option>
            <option value="coverage">Coverage</option>
          </select>
        </QueueField>
        <QueueField label="Queue">
          <select
            value={props.queue}
            onChange={(event) => props.onQueue(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All queues</option>
            <For each={props.queueNames}>
              {(name) => <option value={name}>{name}</option>}
            </For>
          </select>
        </QueueField>
        <QueueField label="Scheduler">
          <select
            value={props.scheduler}
            onChange={(event) => props.onScheduler(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          >
            <option value="">All schedulers</option>
            <For each={props.schedulers}>
              {(item) => <option value={item.id}>{item.name}</option>}
            </For>
          </select>
        </QueueField>
        <QueueField label="Worker ID">
          <input
            value={props.worker}
            onInput={(event) => props.onWorker(event.currentTarget.value)}
            placeholder="Exact or known worker"
            class={QUEUE_CONTROL_CLASS}
          />
        </QueueField>
        <QueueField label="From">
          <input
            type="datetime-local"
            value={props.from}
            onChange={(event) => props.onFrom(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          />
        </QueueField>
        <QueueField label="To">
          <input
            type="datetime-local"
            value={props.to}
            onChange={(event) => props.onTo(event.currentTarget.value)}
            class={QUEUE_CONTROL_CLASS}
          />
        </QueueField>
        <div class="flex items-end">
          <button
            disabled={!props.activeCount}
            onClick={props.onClear}
            class="h-10 w-full rounded-lg border border-gray-700 px-3 text-sm text-gray-300 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear filters
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityList(props: {
  title: string;
  description: string;
  total: number;
  action?: JSX.Element;
  page: number;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  children: JSX.Element;
}) {
  return (
    <div class="overflow-hidden rounded-xl border border-gray-800 bg-gray-950/30">
      <div class="border-b border-gray-800 bg-gray-950/60 px-4 py-3">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div class="flex items-center gap-2">
              <h4 class="font-medium text-gray-200">{props.title}</h4>
              <span class="rounded-full bg-gray-800 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-300">
                {props.total.toLocaleString()}
              </span>
            </div>
            <p class="mt-1 text-xs leading-5 text-gray-600">
              {props.description}
            </p>
          </div>
          <Show when={props.action}>
            <div class="flex flex-wrap gap-2">{props.action}</div>
          </Show>
        </div>
      </div>
      <div class="divide-y divide-gray-800">{props.children}</div>
      <Pager
        page={props.page}
        hasNext={props.hasNext}
        onPrevious={props.onPrevious}
        onNext={props.onNext}
      />
    </div>
  );
}

function HistoryCleanupActions(props: {
  recordType: HistoryCleanupRequest["recordType"];
  busy: boolean;
  onRequest: (request: HistoryCleanupRequest) => void;
}) {
  const logical = () => props.recordType === "logical_executions";
  const buttonClass =
    "rounded-lg border border-gray-700 px-2.5 py-1.5 text-xs text-gray-300 hover:bg-gray-800 disabled:opacity-40";
  return (
    <>
      <button
        disabled={props.busy}
        onClick={() =>
          props.onRequest({ recordType: props.recordType, activity: "empty" })
        }
        class={buttonClass}
      >
        {logical() ? "Clear empty" : "Clear idle"}
      </button>
      <button
        disabled={props.busy}
        onClick={() =>
          props.onRequest({ recordType: props.recordType, activity: "work" })
        }
        class={buttonClass}
      >
        Clear with work
      </button>
      <button
        disabled={props.busy}
        onClick={() =>
          props.onRequest({ recordType: props.recordType, activity: "all" })
        }
        class="rounded-lg border border-red-900/80 px-2.5 py-1.5 text-xs text-red-300 hover:bg-red-950/40 disabled:opacity-40"
      >
        Clear all history
      </button>
    </>
  );
}

function EmptyActivity(props: { text: string }) {
  return (
    <div class="px-4 py-8 text-center text-sm text-gray-600">{props.text}</div>
  );
}

function Notice(props: { tone: "success" | "error"; text: string }) {
  return (
    <div
      class={`rounded-xl border px-4 py-3 text-sm ${props.tone === "success" ? "border-emerald-800/70 bg-emerald-950/40 text-emerald-200" : "border-red-800/70 bg-red-950/40 text-red-200"}`}
    >
      {props.text}
    </div>
  );
}

function ExecutionModal(props: {
  run: TaskQueueExecutionRunDetail;
  onClose: () => void;
}) {
  return (
    <div class="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/75 p-4">
      <div class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-500">
              Execution detail
            </div>
            <h4 class="mt-1 font-semibold text-gray-100">Execution trace</h4>
            <div class="mt-1 font-mono text-xs text-gray-500">
              {props.run.id}
            </div>
          </div>
          <button
            onClick={props.onClose}
            class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
          >
            Close
          </button>
        </div>
        <div class="mt-5 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div class="rounded-lg bg-gray-950/60 p-3">
            <span class="text-xs text-gray-500">Trigger</span>
            <div class="mt-1 capitalize text-gray-200">
              {props.run.triggerKind}
            </div>
          </div>
          <div class="rounded-lg bg-gray-950/60 p-3">
            <span class="text-xs text-gray-500">Queue</span>
            <div class="mt-1 text-gray-200">
              {props.run.queueName ?? "All queues"}
            </div>
          </div>
          <div class="rounded-lg bg-gray-950/60 p-3">
            <span class="text-xs text-gray-500">Worker</span>
            <div class="mt-1 truncate text-gray-200">
              {props.run.workerId ?? "Not assigned"}
            </div>
          </div>
          <div class="rounded-lg bg-gray-950/60 p-3">
            <span class="text-xs text-gray-500">Actor</span>
            <div class="mt-1 text-gray-200">{props.run.actor ?? "System"}</div>
          </div>
        </div>
        <h5 class="mt-6 font-medium text-gray-200">
          Task attempts ({props.run.attempts.length})
        </h5>
        <div class="mt-2 divide-y divide-gray-800 rounded-xl border border-gray-800">
          <For each={props.run.attempts}>
            {(attempt) => (
              <div class="grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-4 py-3 text-sm">
                <div class="min-w-0">
                  <div class="truncate font-mono text-xs text-gray-300">
                    {attempt.taskId}
                  </div>
                  <div class="mt-1 text-xs text-gray-500">
                    Attempt {attempt.attemptNumber} -{" "}
                    {attempt.workerId ?? "unknown worker"}
                  </div>
                </div>
                <QueueStatusBadge status={attempt.status} />
              </div>
            )}
          </For>
          <Show when={!props.run.attempts.length}>
            <div class="px-4 py-5 text-sm text-gray-500">
              No task attempts were claimed in this execution.
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}

function Pager(props: {
  page: number;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div class="flex items-center justify-between border-t border-gray-800 px-4 py-3 text-xs text-gray-500">
      <button
        disabled={props.page <= 1}
        onClick={props.onPrevious}
        class="rounded px-2 py-1 text-gray-300 hover:bg-gray-800 disabled:opacity-30"
      >
        Previous
      </button>
      <span>Cursor page {props.page}</span>
      <button
        disabled={!props.hasNext}
        onClick={props.onNext}
        class="rounded px-2 py-1 text-gray-300 hover:bg-gray-800 disabled:opacity-30"
      >
        Next
      </button>
    </div>
  );
}
